// The editor Write has open, for what the Revise room does to text (the Mac's `rewritingText` rule, review 2026-09-27
// ▸ Open documents): a fragment shown in the editor is never rewritten under it. A finding's Fix, Fix all and Reject go
// through the editor instead, as the writer's own edit — one undoable step, made of small edits so
// comment anchors hold — and everything else goes to the documents. Write registers its editor here while it's shown;
// revision mode's commands (Next and Previous Change, from the Project menu) reach Write's card through it too.
import { useSyncExternalStore } from 'react';

export interface OpenEditor {
  /** The live project the editor writes to (a store change for another project never goes through it). */
  readonly live: object;
  /** The fragments shown. */
  fragmentIds(): string[];
  /** A shown fragment's text as the editor has it. */
  text(fragmentId: string): string | null;
  /** Replaces a shown fragment's text as the writer's own edit (undoable). False if it couldn't (read-only). */
  replaceText(fragmentId: string, text: string): boolean;
  /** Replaces a passage (UTF-16) as the writer's own edit, selecting what went in. */
  replaceRange(fragmentId: string, from: number, to: number, insert: string): boolean;
}

/** Revision mode in Write: the open text's changes, and moving between them. */
export interface RevisionControls {
  changeCount: number;
  /** Moves to the next (or previous) change, selecting it. False when there's none. */
  reveal(forward: boolean): boolean;
}

let editor: OpenEditor | null = null;
let controls: RevisionControls | null = null;
let version = 0;
const listeners = new Set<() => void>();
const changed = () => {
  version++;
  for (const fn of listeners) fn();
};

/** Write's editor while it's shown; the returned function unregisters it. */
export function registerOpenEditor(e: OpenEditor): () => void {
  editor = e;
  changed();
  return () => {
    if (editor === e) {
      editor = null;
      changed();
    }
  };
}

/** The editor open on this live project, if any. */
export function openEditor(live: object | null | undefined): OpenEditor | null {
  return editor && live && editor.live === live ? editor : null;
}

/** The fragments open in the editor for this project. */
export function openFragments(live: object | null | undefined): Set<string> {
  return new Set(openEditor(live)?.fragmentIds() ?? []);
}

export function setRevisionControls(c: RevisionControls | null) {
  if (controls === c) return;
  controls = c;
  changed();
}

export function revisionControls(): RevisionControls | null {
  return controls;
}

/** Re-renders when the open editor or revision controls change. */
export function useOpenEditor(): number {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => version,
  );
}
